import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  ArrowRight,
  Loader2,
  GitFork,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Ban,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { Order } from '../../api/orders';
import StatusBadge from '../common/StatusBadge';

// ── Status icon ──

const STATUS_ICON: Record<string, typeof CheckCircle> = {
  completed: CheckCircle,
  failed: XCircle,
  error: AlertTriangle,
  canceled: Ban,
  processing: Clock,
  pending: Clock,
  created: FileText,
};

function statusIcon(status: string) {
  const key = status.toLowerCase();
  return Object.entries(STATUS_ICON).find(([k]) => key.includes(k))?.[1] || FileText;
}

// ── Tree node type ──

interface TreeNode {
  order: Order;
  children: TreeNode[];
}

function buildTree(orders: Order[]): TreeNode | null {
  if (orders.length === 0) return null;

  // Find the root: the order without a parent, or whose parent is not in our set
  const urnSet = new Set(orders.map((o) => o.urn));
  const root = orders.find((o) => !o.parent || !urnSet.has(o.parent));
  if (!root) return { order: orders[0], children: [] };

  const childMap = new Map<string, Order[]>();
  for (const o of orders) {
    if (o.parent && o.urn !== root.urn) {
      const existing = childMap.get(o.parent) || [];
      existing.push(o);
      childMap.set(o.parent, existing);
    }
  }

  function buildNode(order: Order): TreeNode {
    const kids = childMap.get(order.urn) || [];
    // Sort children by createdAt
    kids.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    return { order, children: kids.map(buildNode) };
  }

  return buildNode(root);
}

// ── Truncate ID ──

function shortId(urn: string): string {
  const id = urn.split(':').pop() || urn;
  if (id.length <= 12) return id;
  return id.slice(0, 6) + '...' + id.slice(-4);
}

// ── Component ──

interface Props {
  orders: Order[];
  currentOrderUrn: string;
  isLoading?: boolean;
  resolveProductUrn?: (urn: string) => string;
}

export default function CascadeOrderDiagram({ orders, currentOrderUrn, isLoading, resolveProductUrn }: Props) {
  const navigate = useNavigate();

  const tree = useMemo(() => buildTree(orders), [orders]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading cascade...
      </div>
    );
  }

  if (!tree) {
    return <p className="text-sm text-gray-500">No cascade data available.</p>;
  }

  // Flatten tree to count total
  function countNodes(node: TreeNode): number {
    return 1 + node.children.reduce((sum, c) => sum + countNodes(c), 0);
  }
  const totalOrders = countNodes(tree);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <GitFork className="h-3.5 w-3.5 text-purple-400" />
        <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider">Cascade Chain</span>
        <span className="text-[10px] text-gray-500">{totalOrders} order{totalOrders !== 1 ? 's' : ''}</span>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-flex flex-col min-w-0">
          <CascadeNode
            node={tree}
            currentUrn={currentOrderUrn}
            depth={0}
            isLast={true}
            navigate={navigate}
            resolveProductUrn={resolveProductUrn}
          />
        </div>
      </div>
    </div>
  );
}

// ── Recursive node renderer ──

function CascadeNode({
  node,
  currentUrn,
  depth,
  isLast,
  navigate,
  resolveProductUrn,
}: {
  node: TreeNode;
  currentUrn: string;
  depth: number;
  isLast: boolean;
  navigate: (path: string) => void;
  resolveProductUrn?: (urn: string) => string;
}) {
  const isCurrent = node.order.urn === currentUrn;
  const Icon = statusIcon(node.order.status);
  const product = resolveProductUrn ? resolveProductUrn(node.order.product) : node.order.product?.split(':').pop() || '-';

  return (
    <div className={clsx(depth > 0 && 'ml-8 relative')}>
      {/* Connector line from parent */}
      {depth > 0 && (
        <>
          {/* Vertical line from parent's bottom edge */}
          <div
            className="absolute w-[2px] bg-purple-500/30"
            style={{ left: '-20px', top: '0', height: '20px' }}
          />
          {/* Horizontal line to this node */}
          <div
            className="absolute h-[2px] bg-purple-500/30"
            style={{ left: '-20px', top: '20px', width: '20px' }}
          />
          {/* Vertical continuation for siblings (unless last) */}
          {!isLast && (
            <div
              className="absolute w-[2px] bg-purple-500/30"
              style={{ left: '-20px', top: '0', bottom: '0' }}
            />
          )}
        </>
      )}

      {/* Order card */}
      <button
        onClick={() => {
          if (!isCurrent) navigate(`/orders/${node.order.uuid}`);
        }}
        className={clsx(
          'group flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all w-full mb-2',
          isCurrent
            ? 'border-purple-500/50 bg-purple-500/10 ring-1 ring-purple-500/30 shadow-sm shadow-purple-500/10'
            : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700/50 dark:bg-gray-800/40 dark:hover:border-gray-600 dark:hover:bg-gray-800/70 cursor-pointer',
        )}
      >
        {/* Status icon */}
        <div
          className={clsx(
            'flex items-center justify-center w-8 h-8 rounded-lg shrink-0',
            isCurrent ? 'bg-purple-500/20' : 'bg-gray-100 dark:bg-gray-700/50',
          )}
        >
          <Icon className={clsx('h-4 w-4', isCurrent ? 'text-purple-400' : 'text-gray-400')} />
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={clsx('text-xs font-mono truncate', isCurrent ? 'text-purple-700 dark:text-purple-200 font-semibold' : 'text-gray-700 dark:text-gray-300')}>
              {shortId(node.order.urn)}
            </span>
            <StatusBadge status={node.order.status} className="text-[10px] px-1.5 py-0" />
            {isCurrent && (
              <span className="inline-flex items-center gap-1 text-[10px] text-purple-700 bg-purple-100 dark:text-purple-300 dark:bg-purple-500/20 rounded-full px-2 py-0.5 shrink-0">
                <MapPin className="h-2.5 w-2.5" /> You are here
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-[10px] text-gray-500 truncate">{product}</span>
            {node.order.createdByRule && (
              <span className="text-[10px] text-gray-500">
                rule: <span className="text-gray-600 dark:text-gray-400">{node.order.createdByRule}</span>
              </span>
            )}
            {depth === 0 && (
              <span className="text-[10px] text-purple-400/60 font-medium">root</span>
            )}
          </div>
        </div>

        {/* Navigate arrow (non-current) */}
        {!isCurrent && (
          <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 dark:text-gray-600 dark:group-hover:text-gray-400 transition-colors shrink-0" />
        )}
      </button>

      {/* Children */}
      {node.children.length > 0 && (
        <div>
          {node.children.map((child, i) => (
            <CascadeNode
              key={child.order.urn}
              node={child}
              currentUrn={currentUrn}
              depth={depth + 1}
              isLast={i === node.children.length - 1}
              navigate={navigate}
              resolveProductUrn={resolveProductUrn}
            />
          ))}
        </div>
      )}
    </div>
  );
}
